import { KnownHubDetailsDtoType, KnownOrderDtoStatus } from '@groundfloor-platform/mgmt-service-g-sdk';
import { DialogActions, DialogContent } from '@mui/material';
import { LoadingIndicator } from '../../../../../../ui/components/pure/loading-indicator/loading-indicator.component';
import { Colors } from '../../../../../../ui/theme/colors';
import { DeliveryActionProps, useDeliveryAction } from '../delivery-action.hook';
import { useDeliveryActionStyles } from '../delivery-action.styles';
import { DeliverNowModalConfirmationStep } from './deliver-now.modal.confirmation-step';
import { Yes as YesIcon } from '../../../../../../ui/components/pure/icons/Yes.icon';
import { TextButton } from '../../../../../../ui/components/pure/text-button/text-button.component';
import { Alert, AlertTitle } from '@mui/material';
import { QrCode } from '../../../../../../ui/components/pure/qr-code/qr-code.component';
import { useMemo, useState } from 'react';

export function DeliverNowModalContent({
  order,
  state,
  selectedHub,
  selectedDeliveryContainer,
  hubSelectOptions,
  hubContainerSelectOptions,
  handlers,
  qrCode,
  qrCodeWarning,
  confirmationStepSubHeading,
  showBookingConfirmationAlert,
  showRecipientSummary,
  form
}: ReturnType<typeof useDeliveryAction> &
  Pick<DeliveryActionProps, 'order'> & {
    confirmationStepSubHeading?: string;
    showRecipientSummary: boolean;
    showBookingConfirmationAlert: boolean;
  }) {
  // Add the order to state so that it doesn't change when the props change
  const [orderState] = useState(order);

  const submitHandler = useMemo(() => {
    if (selectedHub?.type === KnownHubDetailsDtoType.LockersHub) {
      return handlers.onQrCodeHandoff;
    }
    if (selectedHub?.type === KnownHubDetailsDtoType.SpacesHub) {
      return handlers.onDeliverIntoSpacesHub;
    }
    if (selectedHub?.type === KnownHubDetailsDtoType.KiosklessSpacesHub) {
      return handlers.onDeliverIntoKiosklessSpacesHub;
    }
    // We can't pass an undefined submit handler to the form handler, so if no hub is selected, we just return a noop
    return () => {};
  }, [handlers, selectedHub]);

  if (orderState.status !== KnownOrderDtoStatus.Booked && orderState.status !== KnownOrderDtoStatus.RetrievalRequired) {
    return null;
  }

  const dynamicContent = setDynamicModalContent(
    orderState.status as KnownOrderDtoStatus.Booked | KnownOrderDtoStatus.RetrievalRequired,
    selectedHub?.name || ''
  );

  return (
    <>
      {state.current === 'CONFIRMATION_STEP' ? (
        <DeliverNowModalConfirmationStep
          onAbort={handlers.onClose}
          form={form}
          selectedHub={selectedHub}
          callToActionButtonColor={dynamicContent.callToActionButtonColor}
          callToActionButtonLabel={dynamicContent.callToActionLabel}
          deliveryContainerSelectOptions={hubContainerSelectOptions}
          hubSelectOptions={hubSelectOptions}
          onSkipToQrScan={handlers.onSkipToQrScan}
          showHubSelection={orderState.status === KnownOrderDtoStatus.Booked}
          showQrCodeOption={state.avaialbleActions.deliverViaQrCode}
          qrCodeWarning={qrCodeWarning}
          showDeliverViaCloudOption={state.avaialbleActions.deliverViaCloud}
          skipToQrScanLabel={dynamicContent.skipToQrScanLabel}
          submitHandler={submitHandler}
          subHeading={confirmationStepSubHeading}
          order={orderState}
          showRecipientSummary={showRecipientSummary}
          showBookingConfirmationAlert={showBookingConfirmationAlert}
        />
      ) : null}
      {state.current === 'CONNECTING_TO_KIOSK' ? <ConnectingToKioskView selectedHub={selectedHub} /> : null}
      {state.current === 'SENDING_DELIVERY_REQUEST' ? <SendingDeliveryRequestView /> : null}
      {state.current === 'DELIVERY_COMPLETE' ? (
        <DeliveryCompleteView
          dynamicContent={dynamicContent}
          selectedDeliveryContainer={selectedDeliveryContainer}
          handlers={handlers}
        />
      ) : null}
      {state.current === 'HANDOVER_COMPLETE' ? <HandoverCompleteView dynamicContent={dynamicContent} /> : null}
      {state.current === 'FAILED_TO_CONNECT' || state.current === 'SKIPPED_TO_SCAN' ? (
        <QrCodeScanView
          dynamicContent={dynamicContent}
          handlers={handlers}
          state={state}
          qrCode={qrCode}
          qrCodeWarning={qrCodeWarning}
        />
      ) : null}
      {state.current === 'ERROR' ? <ErrorView state={state} handlers={handlers} /> : null}
    </>
  );
}

function ConnectingToKioskView({ selectedHub }: Pick<ReturnType<typeof useDeliveryAction>, 'selectedHub'>) {
  const classes = useDeliveryActionStyles();
  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.dialogContentFullHeightCentered}>
          <LoadingIndicator size="small" />
          <p className={classes.dialogContentSpinnerTitle}>
            Handing off to the the <br />
            {selectedHub?.name || ''} hub
          </p>
        </div>
      </DialogContent>
      <DialogActions className={[classes.dialogActions, classes.dialogActionsEmpty].join(' ')}></DialogActions>
    </>
  );
}

function SendingDeliveryRequestView() {
  const classes = useDeliveryActionStyles();
  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.dialogContentFullHeightCentered}>
          <LoadingIndicator size="small" />
          <p className={classes.dialogContentSpinnerTitle}>Confirming your delivery</p>
        </div>
      </DialogContent>
      <DialogActions className={[classes.dialogActions, classes.dialogActionsEmpty].join(' ')}></DialogActions>
    </>
  );
}

function DeliveryCompleteView({
  dynamicContent,
  selectedDeliveryContainer,
  handlers
}: Pick<ReturnType<typeof useDeliveryAction>, 'selectedDeliveryContainer' | 'handlers'> & {
  dynamicContent: DynamicModalContent;
}) {
  const classes = useDeliveryActionStyles();
  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.dialogContentFullHeightCentered}>
          <YesIcon />
          <p className={classes.dialogContentSpinnerTitle}>{selectedDeliveryContainer?.name || 'space'}</p>
          <p className={classes.dialogContentSpinnerMessage}>Place you're item, and you're all set.</p>
        </div>
      </DialogContent>
      <DialogActions className={[classes.dialogActions, classes.dialogActionsEmpty].join(' ')}>
        <TextButton
          type="submit"
          className={classes.actionButton}
          style={{ color: '#ffffff', backgroundColor: dynamicContent.callToActionButtonColor }}
          fullWidth
          color="primary"
          fontSize="1rem"
          variant="contained"
          onClick={handlers.onComplete}
        >
          {dynamicContent.completedLabel}
        </TextButton>
      </DialogActions>
    </>
  );
}

function HandoverCompleteView({ dynamicContent }: { dynamicContent: DynamicModalContent }) {
  const classes = useDeliveryActionStyles();
  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.dialogContentFullHeightCentered}>
          <YesIcon />
          <p className={classes.dialogContentSpinnerTitle}>{dynamicContent.connectedMessage}</p>
          <p className={classes.dialogContentSpinnerMessage}>
            {dynamicContent.connectedSubtext.line1} <br /> {dynamicContent.connectedSubtext.line2}
          </p>
        </div>
      </DialogContent>
      <DialogActions className={[classes.dialogActions, classes.dialogActionsEmpty].join(' ')}></DialogActions>
    </>
  );
}

function QrCodeScanView({
  handlers,
  qrCode,
  qrCodeWarning,
  state,
  dynamicContent
}: Pick<ReturnType<typeof useDeliveryAction>, 'qrCode' | 'qrCodeWarning' | 'handlers' | 'state'> & {
  dynamicContent: DynamicModalContent;
}) {
  const classes = useDeliveryActionStyles();

  const error =
    qrCode === undefined && !qrCodeWarning
      ? {
          title: 'Unable to display QR Code',
          details: 'Sorry there was an error loading the QR Code, please close the modal and try again'
        }
      : 'error' in state
      ? state.error
      : undefined;

  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.dialogContentFullHeight}>
          {qrCode === undefined && qrCodeWarning ? (
            <p>
              <Alert variant="outlined" severity="warning">
                <AlertTitle>Project not configured</AlertTitle>
                {qrCodeWarning}
              </Alert>
            </p>
          ) : null}
          {error ? (
            <p>
              <Alert variant="outlined" severity="error">
                <AlertTitle>{error.title}</AlertTitle>
                {error.details}
              </Alert>
            </p>
          ) : null}
          {qrCode ? (
            <>
              <p style={{ fontWeight: 600, fontSize: '12px', marginTop: '16px' }}>{dynamicContent.failedMessage}</p>
              <div style={{ display: 'flex', width: '100%', justifyContent: 'center', padding: '16px 0' }}>
                <QrCode qrCode={qrCode} />
              </div>
              <p style={{ marginTop: '16px', fontSize: '12px', lineHeight: 1.5 }}>
                {dynamicContent.qrScanDisclaimer.line1}
              </p>
              <p style={{ fontSize: '12px', lineHeight: 1.5 }}>{dynamicContent.qrScanDisclaimer.line2}</p>
            </>
          ) : null}
        </div>
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
        <TextButton fullWidth color="secondary" variant="outlined" fontSize="1rem" onClick={handlers.onRestart}>
          Back
        </TextButton>
        <TextButton fullWidth color="secondary" variant="outlined" fontSize="1rem" onClick={handlers.onClose}>
          Close
        </TextButton>
      </DialogActions>
    </>
  );
}

function ErrorView({ handlers, state }: Pick<ReturnType<typeof useDeliveryAction>, 'handlers' | 'state'>) {
  const classes = useDeliveryActionStyles();

  const error =
    'error' in state
      ? state.error
      : {
          title: 'Unable to perform request',
          details: 'Please close the modal and try again'
        };

  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <Alert variant="outlined" severity="error">
          <AlertTitle>{error.title}</AlertTitle>
          {error.details}
        </Alert>
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
        <TextButton color="secondary" variant="outlined" fontSize="1rem" onClick={handlers.onClose}>
          Close
        </TextButton>
      </DialogActions>
    </>
  );
}

interface DynamicModalContent {
  skipToQrScanLabel: string;
  callToActionLabel: string;
  callToActionButtonColor: string;
  completedLabel: string;
  confirmLabel: string;
  connectedMessage: string;
  connectedSubtext: {
    line1: string;
    line2: string;
  };
  failedMessage: string;
  qrScanDisclaimer: {
    line1: string;
    line2: string;
  };
}

export function setDynamicModalContent(
  status: KnownOrderDtoStatus.Booked | KnownOrderDtoStatus.RetrievalRequired,
  hubName: string
): DynamicModalContent {
  const content: Record<KnownOrderDtoStatus.Booked | KnownOrderDtoStatus.RetrievalRequired, DynamicModalContent> = {
    booked: {
      callToActionLabel: 'Start Delivery',
      callToActionButtonColor: Colors.deliveredBlue,
      completedLabel: 'Done',
      confirmLabel: 'Confirm',
      skipToQrScanLabel: 'Deliver with QR code',
      connectedMessage: 'Ready for delivery',
      connectedSubtext: {
        line1: 'Please complete your delivery on',
        line2: 'the locker touchscreen.'
      },
      failedMessage: `Scan this code at the ${hubName} lockers to deliver`,
      qrScanDisclaimer: {
        line1:
          'If the lockers are currently offline, the status of the order may not update immediately once delivered.',
        line2: `No need to worry - if the lockers have accepted the item you're all done!`
      }
    },
    'retrieval-required': {
      callToActionLabel: 'Retrieve Now',
      callToActionButtonColor: Colors.retrievalRequiredPurple,
      completedLabel: 'Done',
      confirmLabel: 'Confirm',
      skipToQrScanLabel: 'Retrieve with QR code',
      connectedMessage: 'Ready for retrieval',
      connectedSubtext: {
        line1: 'Please see the locker touchscreen',
        line2: 'for confirmation.'
      },
      failedMessage: 'Please scan this code at the lockers to retrieve',
      qrScanDisclaimer: {
        line1:
          'If the lockers are currently offline, the status of the order may not update immediately once retrieved.',
        line2: `No need to worry - just grab the item, close the door and you're all done!`
      }
    }
  };
  return content[status];
}
